import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { Autocomplete, Container, Grid, TextField } from "@mui/material";
import CustomTextField from "../../components/CustomTextField";
import { ReactComponent as ArrowLeftSvg } from "./../../assets/svg/arrow-left.svg";
import RoundButton from "../../components/RoundButton";
import { useDispatch, useSelector } from "react-redux";
import {
  postalCodeValidationRequest,
  signUpRequest,
  stateListRequest,
} from "../../redux/signup/action";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import companyDetailScheme from "../../validations/companyDetailScheme";
import { updateCompanyDetailsFrom } from "../../redux/signup/constants";
import CustomLoader from "../../components/CustomLoader";
import companyObject from "../../utils";
import background_image from "./../../assets/png/background_image.png";
import { useResponsive } from "../../hooks/useResponsive";
import bigLogo from "./../../assets/safe_logo.png";
import storePersist, { localConstant } from "../../services/localStorage";
import { useTranslation } from "react-i18next";
import { COUNTRY_CODE } from "../../utils/utils";

const CompanyDetailsPage = () => {
  const { isLoading, companyDetailsData, createAccount, stateObject } =
    useSelector((state) => state.signUpReducer);

  const signUpUserDetails = storePersist.get(
    localConstant.OWNER_SIGN_UP_DETAILS,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const smDown = useResponsive("down", "sm");
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
    defaultValues: {},
    resolver: yupResolver(companyDetailScheme),
  });

  useEffect(() => {
    dispatch(stateListRequest({}, () => {}));
  }, []);

  const formCompanyDetails = (values) => {
    dispatch(updateCompanyDetailsFrom(values));

    let data = {
      ...companyObject,
      ["phoneNumber"]: signUpUserDetails?.phoneNumber,
      ["password"]: signUpUserDetails?.password,
      ["userEmail"]: signUpUserDetails?.userEmail,
      phone: {
        intlDialCd: signUpUserDetails?.countryCode,
        phoneNumber: signUpUserDetails?.phoneNumber,
      },
      ...values,
    };

    const postalParamData = {
      addressLine1: values?.addressLine1,
      state: values?.stateCode,
      country: COUNTRY_CODE[createAccount?.countryCode],
      postalCode: values?.postalCode,
    };

    dispatch(
      postalCodeValidationRequest(postalParamData, (response) => {
        if (response?.postalCode) {
          dispatch(
            signUpRequest(data, (res) => {
              if (res?.user) {
                storePersist.set(
                  localConstant.OWNER_USER_ID,
                  res?.user?.userId,
                );
                storePersist.set(
                  localConstant.OWNER_CUSTOMER_ID,
                  res?.user?.customerId,
                );
                navigate("/emailVerification");
              }
            }),
          );
        }
      }),
    );
  };

  const renderCompanyDetails = (
    <>
      <Grid container sx={{ mb: 3 }} direction={"row"}>
        <ArrowLeftSvg
          style={{ alignSelf: "center" }}
          onClick={() => {
            navigate("/signup", {
              state: {
                newUser: false,
              },
            });
          }}
        />
        <Typography sx={{ ml: 1 }} variant="titleText" color="primary.main">
          {t("pages.companyDetails.pageHeading")}
        </Typography>
      </Grid>
      <Stack spacing={1}>
        <Stack spacing={1} direction={smDown ? "column" : "row"}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <CustomTextField
                {...field}
                required
                placeholder={t("pages.companyDetails.text01")}
                label={t("pages.companyDetails.text01")}
                name="firstName"
                type="text"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            )}
          ></Controller>
          <Controller
            name="middleName"
            control={control}
            render={({ field }) => (
              <CustomTextField
                {...field}
                placeholder={t("pages.companyDetails.text02")}
                label={t("pages.companyDetails.text02")}
                name="middleName"
                type="text"
              />
            )}
          ></Controller>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <CustomTextField
                {...field}
                required
                placeholder={t("pages.companyDetails.text03")}
                label={t("pages.companyDetails.text03")}
                name="lastName"
                type="text"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            )}
          ></Controller>
        </Stack>
        <Controller
          name="companyName"
          control={control}
          render={({ field }) => (
            <CustomTextField
              {...field}
              required
              placeholder={t("pages.companyDetails.text04")}
              label={t("pages.companyDetails.text04")}
              name="companyName"
              type="text"
              error={!!errors.companyName}
              helperText={errors.companyName?.message}
            />
          )}
        ></Controller>
        <Controller
          name="addressLine1"
          control={control}
          render={({ field }) => (
            <CustomTextField
              {...field}
              required
              placeholder={t("pages.companyDetails.text05")}
              label={t("pages.companyDetails.text05")}
              name="addressLine1"
              type="text"
              error={!!errors.addressLine1}
              helperText={errors.addressLine1?.message}
            />
          )}
        ></Controller>
        <Stack spacing={1} direction={smDown ? "column" : "row"}>
          <Controller
            name="postalCode"
            control={control}
            render={({ field }) => (
              <CustomTextField
                label={t("pages.companyDetails.text06")}
                {...field}
                placeholder={t("pages.companyDetails.text06")}
                required
                name="postalCode"
                type="tel"
                error={!!errors.postalCode}
                helperText={errors.postalCode?.message}
              />
            )}
          ></Controller>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <CustomTextField
                label={t("pages.companyDetails.text07")}
                {...field}
                required
                placeholder={t("pages.companyDetails.text07")}
                name="city"
                type="text"
                error={!!errors.city}
                helperText={errors.city?.message}
              />
            )}
          ></Controller>
          <Controller
            name="state"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box width={smDown ? "auto" : 0.38}>
                <Typography
                  variant="customLable"
                  color="common.inputCustomLable"
                  marginBottom={1}
                  style={{ fontSize: "14px" }}
                >
                  {t("pages.companyDetails.text08")}
                  <span style={{ color: "#C50F1F" }}>*</span>
                </Typography>

                <Autocomplete
                  name="state"
                  options={stateObject?.USA?.map((item, index) => {
                    return {
                      value: item?.code,
                      label: item?.description,
                    };
                  })}
                  inputValue={companyDetailsData?.state}
                  getOptionLabel={(option) => option?.label}
                  isOptionEqualToValue={(option, value) =>
                    option?.label === value?.label
                  }
                  onChange={(event, item) => {
                    onChange(item?.label);
                    setValue("stateCode", item?.value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      name="state"
                      {...params}
                      error={!!errors.state}
                      helperText={errors.state?.message}
                      fullWidth
                    />
                  )}
                />
              </Box>
            )}
          ></Controller>
        </Stack>
      </Stack>
    </>
  );

  return (
    <Container
      disableGutters
      maxWidth={false}
      style={{
        paddingTop: 20,
        position: "relative",
        minHeight: "100vh",
        backgroundColor: "#80BCFF4F",
        backgroundImage: `url(${background_image})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        height: "100vh",
      }}
    >
      <Box
        maxWidth="xs"
        sx={{
          height: 1,
          flexGrow: 1,
          minHeight: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            height: 1,
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            flexDirection: ["column", "column", "row"],
          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: 1, width: 1, padding: 0, gap: 5 }}
          >
            <img src={bigLogo} alt="Your Alt Text" />
            <Card
              sx={{
                p: 3,
                pb: 0,
                width: 1,
                maxWidth: 600,
                height: 1,
                maxHeight: !smDown ? 550 : "auto",
              }}
              style={{ overflow: "auto" }}
            >
              <form onSubmit={handleSubmit((data) => formCompanyDetails(data))}>
                {renderCompanyDetails}
                <RoundButton
                  sx={{ mt: 5, mb: 5 }}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  color="inherit"
                >
                  <Typography variant="buttonText" color="common.buttonText">
                    {t("commonLabels.text01")}
                  </Typography>
                </RoundButton>
              </form>
            </Card>
          </Stack>
        </Box>
      </Box>
      {isLoading && <CustomLoader />}
    </Container>
  );
};

export default CompanyDetailsPage;
